import { useState, useMemo } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Spinner,
} from '@heroui/react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import InlineEdit from '@/components/common/InlineEdit';
import { useAreas, useCreateArea, useUpdateArea, useDeleteArea } from '@/api/areas';
import type { Area } from '@estimator/shared';

interface AreaTableProps {
  projectId: string;
}

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'primary'> = {
  active: 'success',
  pending: 'warning',
  deleted: 'danger',
};

function AiBadge() {
  return (
    <Chip
      size="sm"
      variant="flat"
      color="success"
      classNames={{
        base: 'h-4 min-w-0 px-1',
        content: 'text-[9px] font-ui font-bold px-0',
      }}
    >
      AI
    </Chip>
  );
}

function OverrideBadge() {
  return (
    <Chip
      size="sm"
      variant="flat"
      color="primary"
      classNames={{
        base: 'h-4 min-w-0 px-1',
        content: 'flex items-center gap-0.5 text-[9px] px-0',
      }}
    >
      <Pencil size={8} />
    </Chip>
  );
}

function GroupHeaderRow({ level, span }: { level: string; span: number }) {
  return (
    <TableRow
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <TableCell
        colSpan={span}
        className="py-1.5 px-4"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <span
          className="text-xs font-semibold uppercase tracking-wider font-ui"
          style={{ color: 'var(--accent)' }}
        >
          {level}
        </span>
      </TableCell>
    </TableRow>
  );
}

function NumericCell({
  value,
  areaId,
  field,
  projectId,
  aiExtracted,
  overridden,
  onSave,
}: {
  value: number | null | undefined;
  areaId: string;
  field: string;
  projectId: string;
  aiExtracted: boolean;
  overridden: boolean;
  onSave: (val: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <InlineEdit
        value={value != null ? Number(value.toFixed(2)) : value}
        onSave={onSave}
        type="number"
        step={0.01}
        className="font-ui"
      />
      {aiExtracted && !overridden && <AiBadge />}
      {overridden && <OverrideBadge />}
    </div>
  );
}

export default function AreaTable({ projectId }: AreaTableProps) {
  const { data: areas, isLoading, isError } = useAreas(projectId);
  const createArea = useCreateArea(projectId);
  const updateArea = useUpdateArea();
  const deleteArea = useDeleteArea();

  const [addingLevel, setAddingLevel] = useState<string | null>(null);

  const grouped = useMemo(() => {
    if (!areas) return [];
    const map = new Map<string, Area[]>();
    for (const area of areas) {
      const key = area.level || 'Unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(area);
    }
    return Array.from(map.entries()).map(([level, rows]) => ({ level, rows }));
  }, [areas]);

  function handleUpdate(area: Area, field: keyof Area, raw: string) {
    const numericFields = ['length', 'width', 'height', 'area', 'perimeter'];
    const value = numericFields.includes(field)
      ? raw === '' ? null : parseFloat(raw)
      : raw;

    updateArea.mutate({
      id: area.id,
      projectId,
      [field]: value,
      overridden: numericFields.includes(field) ? true : area.overridden,
    });
  }

  function handleAddArea() {
    const level = addingLevel ?? (grouped[0]?.level ?? 'Ground Floor');
    createArea.mutate({ name: 'New Area', level, unit: 'm²' });
  }

  function handleDelete(area: Area) {
    if (confirm(`Delete area "${area.name}"?`)) {
      deleteArea.mutate({ id: area.id, projectId });
    }
  }

  const columns = [
    { key: 'name', label: 'Area Name' },
    { key: 'length', label: 'L (m)' },
    { key: 'width', label: 'W (m)' },
    { key: 'height', label: 'H (m)' },
    { key: 'area', label: 'Area (m²)' },
    { key: 'perimeter', label: 'Perimeter (m)' },
    { key: 'source_drawing', label: 'Source' },
    { key: 'notes', label: 'Notes' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="md" color="primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm" style={{ color: 'var(--red)' }}>
          Failed to load areas.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            Areas
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {areas?.length ?? 0} areas — click any cell to edit inline
          </p>
        </div>
      </div>

      <div
        className="rounded-xl border overflow-hidden"
        style={{ borderColor: 'var(--border)' }}
      >
        <Table
          removeWrapper
          aria-label="Areas table"
          classNames={{
            table: 'min-w-full',
            th: 'bg-[var(--bg-base)] text-[10px] uppercase tracking-wider font-ui text-[var(--text-muted)] border-b border-[var(--border)] h-9 px-3',
            td: 'py-2 px-3 border-b border-[var(--border)] text-xs align-middle',
          }}
        >
          <TableHeader columns={columns}>
            {(col) => (
              <TableColumn key={col.key} className={col.key === 'actions' ? 'w-10' : ''}>
                {col.label}
              </TableColumn>
            )}
          </TableHeader>

          <TableBody emptyContent={
            <div className="py-12 text-center" style={{ color: 'var(--text-muted)' }}>
              No areas yet. Click Add Area below.
            </div>
          }>
            {grouped.flatMap(({ level, rows }, gi) => [
              // Group header as a special row — using a key unique to each group
              <TableRow
                key={`group-${gi}`}
                style={{ backgroundColor: 'var(--bg-base)' }}
              >
                {columns.map((col, ci) =>
                  ci === 0 ? (
                    <TableCell
                      key={col.key}
                      style={{ borderBottom: '1px solid var(--border-em)', paddingTop: '8px', paddingBottom: '8px' }}
                    >
                      <span
                        className="text-xs font-semibold uppercase tracking-widest font-ui"
                        style={{ color: 'var(--accent)' }}
                      >
                        {level}
                      </span>
                    </TableCell>
                  ) : (
                    <TableCell
                      key={col.key}
                      style={{ borderBottom: '1px solid var(--border-em)' }}
                    >
                      {' '}
                    </TableCell>
                  )
                )}
              </TableRow>,

              // Data rows
              ...rows.map((area, ri) => (
                <TableRow
                  key={area.id}
                  style={{
                    backgroundColor: ri % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-panel)',
                  }}
                >
                  {/* Area Name */}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <InlineEdit
                        value={area.name}
                        onSave={(v) => handleUpdate(area, 'name', v)}
                      />
                      {area.ai_extracted && !area.overridden && <AiBadge />}
                      {area.overridden && <OverrideBadge />}
                    </div>
                  </TableCell>

                  {/* Length */}
                  <TableCell>
                    <InlineEdit
                      value={area.length != null ? Number(area.length.toFixed(2)) : undefined}
                      onSave={(v) => handleUpdate(area, 'length', v)}
                      type="number"
                      step={0.01}
                      className="font-ui"
                    />
                  </TableCell>

                  {/* Width */}
                  <TableCell>
                    <InlineEdit
                      value={area.width != null ? Number(area.width.toFixed(2)) : undefined}
                      onSave={(v) => handleUpdate(area, 'width', v)}
                      type="number"
                      step={0.01}
                      className="font-ui"
                    />
                  </TableCell>

                  {/* Height */}
                  <TableCell>
                    <InlineEdit
                      value={area.height != null ? Number(area.height.toFixed(2)) : undefined}
                      onSave={(v) => handleUpdate(area, 'height', v)}
                      type="number"
                      step={0.01}
                      className="font-ui"
                    />
                  </TableCell>

                  {/* Area */}
                  <TableCell>
                    <InlineEdit
                      value={area.area != null ? Number(area.area.toFixed(2)) : undefined}
                      onSave={(v) => handleUpdate(area, 'area', v)}
                      type="number"
                      step={0.01}
                      className="font-ui"
                    />
                  </TableCell>

                  {/* Perimeter */}
                  <TableCell>
                    <InlineEdit
                      value={area.perimeter != null ? Number(area.perimeter.toFixed(2)) : undefined}
                      onSave={(v) => handleUpdate(area, 'perimeter', v)}
                      type="number"
                      step={0.01}
                      className="font-ui"
                    />
                  </TableCell>

                  {/* Source drawing */}
                  <TableCell>
                    <InlineEdit
                      value={area.source_drawing}
                      onSave={(v) => handleUpdate(area, 'source_drawing', v)}
                      placeholder="—"
                    />
                  </TableCell>

                  {/* Notes */}
                  <TableCell>
                    <InlineEdit
                      value={area.notes}
                      onSave={(v) => handleUpdate(area, 'notes', v)}
                      placeholder="—"
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      size="sm"
                      variant="dot"
                      color={area.ai_extracted && !area.overridden ? 'success' : area.overridden ? 'primary' : 'default'}
                      classNames={{
                        base: 'h-5 border-none',
                        content: 'text-[10px] font-ui px-1',
                      }}
                    >
                      {area.overridden ? 'Edited' : area.ai_extracted ? 'AI' : 'Manual'}
                    </Chip>
                  </TableCell>

                  {/* Delete */}
                  <TableCell>
                    <button
                      onClick={() => handleDelete(area)}
                      className="p-1 rounded opacity-30 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--red)]"
                      style={{ color: 'var(--red)' }}
                      title="Delete area"
                    >
                      <Trash2 size={13} />
                    </button>
                  </TableCell>
                </TableRow>
              )),
            ])}
          </TableBody>
        </Table>
      </div>

      {/* Add Area */}
      <Button
        size="sm"
        variant="bordered"
        startContent={<Plus size={14} />}
        isLoading={createArea.isPending}
        onPress={handleAddArea}
        className="border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
      >
        Add Area
      </Button>
    </div>
  );
}
